import React from 'react';

import type { ColumnDef, OnChangeFn, Row, SortingState, VisibilityState } from '@tanstack/react-table';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  initialColumnVisibility?: VisibilityState;
  /** Initial TanStack sort when not using controlled `sorting`. */
  initialSorting?: SortingState;
  /** Controlled sort state (e.g. sync with ColumnHeader `SortingProps`). */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
}

export function useDataTable<TData, TValue>({
  columns,
  data,
  getRowId,
  initialColumnVisibility,
  initialSorting,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialColumnVisibility ?? {});
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(initialSorting ?? []);
  const sorting = controlledSorting !== undefined ? controlledSorting : internalSorting;
  const onSortingChange = controlledOnSortingChange ?? setInternalSorting;

  return useReactTable({
    data,
    columns,
    getRowId,
    state: {
      columnVisibility,
      rowSelection,
      sorting,
    },

    enableRowSelection: true,
    enableSorting: true,
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
}
